import { useState } from "react";
import type { SanityProduct } from "../../../sanity/types";
import StarRating from "./StarRating";
import { useCart } from "../../../hooks/useCart";

interface ProductInfoProps {
  product: SanityProduct;
}

export default function ProductInfo({ product }: ProductInfoProps) {
  const { addToCart } = useCart();
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const amazonUrl = product.amazonLink || "https://www.amazon.com";
  const TEMP_AMAZON_REDIRECT = true;
  const buttonMode = TEMP_AMAZON_REDIRECT
    ? "buy-now"
    : added
      ? "added"
      : "add-to-cart";
  const buttonClass =
    buttonMode === "added"
      ? "bg-emerald-600 text-white"
      : "bg-[#144E5D] hover:bg-[#B8D8DE] hover:text-[#144E5D] text-white";
  const buttonIcon =
    buttonMode === "buy-now"
      ? "ri-shopping-bag-line"
      : buttonMode === "added"
        ? "ri-check-line"
        : "ri-shopping-cart-line";
  const buttonLabel =
    buttonMode === "buy-now"
      ? "Buy Now"
      : buttonMode === "added"
        ? "Added to Cart!"
        : "Add to Cart";

  const handleAddToCart = () => {
    addToCart(product, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 2500);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Category Badge */}
      <span className="inline-flex w-fit text-xs font-semibold text-[#144E5D] bg-[#144E5D]/10 px-3 py-1 rounded-full">
        {product.category}
      </span>

      {/* Title */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 leading-tight mb-2">
          {product.name}
        </h1>
        <p className="text-base text-gray-500 font-medium">
          {product.subtitle}
        </p>
      </div>
      {/* Product Identifiers */}
      {product.productIdentifiers && (product.productIdentifiers.ndc || product.productIdentifiers.upc) && (
        <div className="flex items-center gap-4 text-xs text-gray-500 -mt-3">
          {product.productIdentifiers.ndc && (
            <span>
              <span className="font-semibold">NDC:</span> {product.productIdentifiers.ndc}
            </span>
          )}
          {product.productIdentifiers.upc && (
            <span>
              <span className="font-semibold">UPC:</span> {product.productIdentifiers.upc}
            </span>
          )}
        </div>
      )}

      {/* Ratings */}
      <div className="flex items-center gap-3">
        <StarRating rating={product.ratingAvg ?? 0} size="md" />
        <span className="text-sm font-semibold text-amber-500">
          {(product.ratingAvg ?? 0).toFixed(1)}
        </span>
        <span className="text-sm text-gray-400">
          {(product.ratingCount ?? 0).toLocaleString()} ratings
        </span>
      </div>



      {/* Divider */}
      <div className="border-t border-gray-100"></div>

      {/* Price */}
      <div className="flex items-baseline gap-3">
        <span className="text-4xl font-bold text-[#144E5D]">
          {product.price}
        </span>
        <span className="text-sm text-gray-400 font-medium">
          {product.perUnit}
        </span>
      </div>

      {/* Features */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wider">
          Key Features
        </h3>
        <ul className="space-y-2">
          {(product.features ?? []).map((feat, idx) => (
            <li
              key={idx}
              className="flex items-start gap-2.5 text-sm text-gray-600"
            >
              <span className="mt-1 w-4 h-4 flex items-center justify-center shrink-0 text-[#144E5D]">
                <i className="ri-check-line text-sm font-bold"></i>
              </span>
              {feat}
            </li>
          ))}
        </ul>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-100"></div>

      {/* Quantity + Add to Cart */}
      <div className="flex items-center gap-4">
        {/* Qty selector */}
        <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setQty((q) => Math.max(1, q - 1))}
            className="w-10 h-11 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <i className="ri-subtract-line"></i>
          </button>
          <span className="w-10 text-center text-sm font-semibold text-gray-800">
            {qty}
          </span>
          <button
            onClick={() => setQty((q) => q + 1)}
            className="w-10 h-11 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <i className="ri-add-line"></i>
          </button>
        </div>

        {/* Add to Cart */}
        <button
          onClick={() => {
            if (TEMP_AMAZON_REDIRECT) {
              // Handle relative URLs (same domain) vs external URLs
              if (amazonUrl.includes('mycurewell.com')) {
                const path = amazonUrl.split('mycurewell.com')[1];
                window.location.href = path;
              } else {
                window.open(amazonUrl, "_blank", "noopener,noreferrer");
              }
              return;
            }
            handleAddToCart();
          }}
          className={`flex-1 py-3 rounded-lg font-semibold text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${buttonClass}`}
        >
          <>
            <i className={buttonIcon}></i>
            {buttonLabel}
          </>
        </button>
      </div>

      {/* Trust badges */}
      <div className="grid grid-cols-3 gap-3 mt-2">
        {[
          { icon: "ri-shield-check-line", label: "GMP Certified" },
          { icon: "ri-truck-line", label: "Fast Shipping" },
          { icon: "ri-medal-line", label: "Quality Assured" },
        ].map(({ icon, label }) => (
          <div
            key={label}
            className="flex flex-col items-center gap-1.5 bg-[#F0F7F8] rounded-lg py-3 px-2"
          >
            <div className="w-6 h-6 flex items-center justify-center text-[#144E5D]">
              <i className={`${icon} text-lg`}></i>
            </div>
            <span className="text-xs text-gray-600 font-medium text-center leading-tight">
              {label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
